import type {
  HelmInquiryStatus,
  InquiryWorkflowPhase,
  WorkflowOperationalState,
} from "@/lib/api/types";

export interface InquiryPhaseStep {
  phase: InquiryWorkflowPhase;
  title: string;
  description: string;
  defaultTask: string;
}

export const INQUIRY_PHASE_STEPS: InquiryPhaseStep[] = [
  {
    phase: "CAPTURED",
    title: "Captured",
    description: "Acknowledge the request and establish the service scope.",
    defaultTask: "Acknowledge inquiry and confirm communication channel",
  },
  {
    phase: "REQUIREMENTS",
    title: "Requirements",
    description: "Confirm products, quantities, documents, budget, and service needs.",
    defaultTask: "Distill requirements and identify supplier options",
  },
  {
    phase: "SUPPLIER_QUOTES",
    title: "Supplier quotes",
    description: "Collect and review supplier quotations with the customer.",
    defaultTask: "Request factory quotations from shortlisted suppliers",
  },
  {
    phase: "EXECUTION",
    title: "Execution",
    description: "Coordinate the approved order, payment, testing, and shipment handoff.",
    defaultTask: "Place order and coordinate factory payment",
  },
  {
    phase: "COMPLETED",
    title: "Completed",
    description: "The inquiry is complete or has moved into an operational consignment.",
    defaultTask: "Close inquiry and archive the request",
  },
];

export const INQUIRY_PHASE_TRANSITIONS: Record<InquiryWorkflowPhase, InquiryWorkflowPhase[]> = {
  CAPTURED: ["REQUIREMENTS"],
  REQUIREMENTS: ["CAPTURED", "SUPPLIER_QUOTES", "EXECUTION"],
  SUPPLIER_QUOTES: ["REQUIREMENTS", "EXECUTION"],
  EXECUTION: ["REQUIREMENTS", "SUPPLIER_QUOTES", "COMPLETED"],
  COMPLETED: ["EXECUTION"],
};

export const WORKFLOW_OPERATIONAL_STATE_TRANSITIONS: Record<
  WorkflowOperationalState,
  WorkflowOperationalState[]
> = {
  ACTIVE: ["BLOCKED", "COMPLETED", "CANCELLED"],
  BLOCKED: ["ACTIVE", "CANCELLED"],
  COMPLETED: ["ACTIVE"],
  CANCELLED: ["ACTIVE"],
};

export function inquiryPhaseStep(phase: InquiryWorkflowPhase) {
  return INQUIRY_PHASE_STEPS.find((step) => step.phase === phase) ?? INQUIRY_PHASE_STEPS[0];
}

export function defaultTaskForInquiryPhase(phase: InquiryWorkflowPhase) {
  return inquiryPhaseStep(phase).defaultTask;
}

export function canTransitionInquiryPhase(from: InquiryWorkflowPhase, to: InquiryWorkflowPhase) {
  return from === to || INQUIRY_PHASE_TRANSITIONS[from].includes(to);
}

export function inquiryPhaseTransitionChoices(from: InquiryWorkflowPhase) {
  return [from, ...INQUIRY_PHASE_TRANSITIONS[from]];
}

export function workflowOperationalStateTransitionChoices(from: WorkflowOperationalState) {
  return [from, ...WORKFLOW_OPERATIONAL_STATE_TRANSITIONS[from]];
}

export interface InquiryWorkflowStep {
  status: HelmInquiryStatus;
  title: string;
  description: string;
  defaultAction: string;
}

export const INQUIRY_WORKFLOW_STEPS: InquiryWorkflowStep[] = [
  {
    status: "DRAFT",
    title: "Draft",
    description: "Internal intake is being prepared before customer acknowledgement.",
    defaultAction: "Complete source capture and submit inquiry",
  },
  {
    status: "SUBMITTED",
    title: "Captured",
    description:
      "Request has been received from WhatsApp, Instagram, web, email, or manual intake.",
    defaultAction: "Acknowledge inquiry and confirm communication channel",
  },
  {
    status: "QUALIFYING",
    title: "Requirements",
    description:
      "Distill product requirements, quantities, documents, supplier candidates, and service scope.",
    defaultAction: "Distill requirements and identify supplier options",
  },
  {
    status: "QUOTED",
    title: "Supplier Quotes",
    description: "Supplier quotations and proforma documents are being reviewed with the customer.",
    defaultAction: "Issue customer proforma and request supplier approval",
  },
  {
    status: "ACTIVE",
    title: "Execution",
    description:
      "Approved supplier/order is active. Handle invoice, payment, PVOC, manufacturing, and shipping handoff.",
    defaultAction: "Place order and coordinate factory payment",
  },
  {
    status: "BLOCKED",
    title: "Blocked",
    description:
      "Work is waiting on missing information, customer approval, supplier response, or payment.",
    defaultAction: "Resolve blocker with customer or supplier",
  },
  {
    status: "COMPLETED",
    title: "Completed",
    description: "Inquiry work is complete or converted into the operating consignment.",
    defaultAction: "Close inquiry and archive communications",
  },
  {
    status: "CANCELLED",
    title: "Cancelled",
    description: "Customer or operations cancelled the request.",
    defaultAction: "Record cancellation reason and notify customer",
  },
];

export const INQUIRY_STATUS_TRANSITIONS: Record<HelmInquiryStatus, HelmInquiryStatus[]> = {
  DRAFT: ["SUBMITTED", "CANCELLED"],
  SUBMITTED: ["QUALIFYING", "BLOCKED", "CANCELLED"],
  QUALIFYING: ["QUOTED", "ACTIVE", "BLOCKED", "CANCELLED"],
  QUOTED: ["ACTIVE", "BLOCKED", "CANCELLED"],
  ACTIVE: ["COMPLETED", "BLOCKED", "CANCELLED"],
  BLOCKED: ["QUALIFYING", "ACTIVE", "CANCELLED"],
  COMPLETED: [],
  CANCELLED: [],
};

export const INQUIRY_NEXT_ACTIONS: Array<{ value: string; statuses: HelmInquiryStatus[] }> = [
  { value: "Complete source capture and submit inquiry", statuses: ["DRAFT"] },
  { value: "Acknowledge inquiry and confirm communication channel", statuses: ["SUBMITTED"] },
  {
    value: "Distill requirements and identify supplier options",
    statuses: ["SUBMITTED", "QUALIFYING"],
  },
  {
    value: "Confirm product quantities, specifications, and target budget",
    statuses: ["SUBMITTED", "QUALIFYING"],
  },
  { value: "Request factory quotations from shortlisted suppliers", statuses: ["QUALIFYING"] },
  { value: "Upload supplier proforma invoice or quotation", statuses: ["QUALIFYING", "QUOTED"] },
  {
    value: "Review supplier quotations and landed-cost assumptions",
    statuses: ["QUALIFYING", "QUOTED"],
  },
  { value: "Issue customer proforma and request supplier approval", statuses: ["QUOTED"] },
  { value: "Confirm selected supplier and place order", statuses: ["QUOTED", "ACTIVE"] },
  { value: "Convert proforma to invoice and request payment", statuses: ["QUOTED", "ACTIVE"] },
  { value: "Place order and coordinate factory payment", statuses: ["ACTIVE"] },
  { value: "Track manufacturing readiness and factory dispatch", statuses: ["ACTIVE"] },
  { value: "Arrange PVOC testing and certification", statuses: ["ACTIVE"] },
  { value: "Arrange shipping handoff for approved service scope", statuses: ["ACTIVE"] },
  { value: "Convert inquiry into operational consignment", statuses: ["ACTIVE", "COMPLETED"] },
  { value: "Resolve blocker with customer or supplier", statuses: ["BLOCKED"] },
  { value: "Close inquiry and archive communications", statuses: ["COMPLETED"] },
  { value: "Record cancellation reason and notify customer", statuses: ["CANCELLED"] },
];

export function inquiryWorkflowStep(status: HelmInquiryStatus) {
  return INQUIRY_WORKFLOW_STEPS.find((step) => step.status === status) ?? INQUIRY_WORKFLOW_STEPS[0];
}

export function inquiryNextActionsForStatus(status: HelmInquiryStatus) {
  const actions = INQUIRY_NEXT_ACTIONS.filter((action) => action.statuses.includes(status));
  return actions.length ? actions : INQUIRY_NEXT_ACTIONS;
}

export function defaultInquiryNextAction(status: HelmInquiryStatus) {
  return inquiryWorkflowStep(status).defaultAction;
}

export function isControlledInquiryNextAction(value: string | null | undefined) {
  return Boolean(value && INQUIRY_NEXT_ACTIONS.some((action) => action.value === value));
}

export function isInquiryNextActionAllowedForStatus(
  status: HelmInquiryStatus,
  value: string | null | undefined,
) {
  return Boolean(
    value && inquiryNextActionsForStatus(status).some((action) => action.value === value),
  );
}

export function canTransitionInquiryStatus(from: HelmInquiryStatus, to: HelmInquiryStatus) {
  return from === to || INQUIRY_STATUS_TRANSITIONS[from].includes(to);
}

export function inquiryStatusTransitionChoices(from: HelmInquiryStatus) {
  return [from, ...INQUIRY_STATUS_TRANSITIONS[from]];
}
